import { useState, useEffect } from "react";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import CardMedia from "@material-ui/core/CardMedia";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import { useAuth } from "./../util/auth";
import { useRouter } from "./../util/router.js";
import { createItem } from "./../util/db";
import { modifiedApiRequest } from "../util/util";
import { CheckBoxOutlined } from "@material-ui/icons";
import PromptWarning from "./PromptWarning.js";
import TimerLoader from "./TimerLoader.js";
import {
  Download,
  Image as ImageIcon,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Video as VideoIcon,
} from "lucide-react";
import { IconButton } from "@material-ui/core";
import { fetchBannedWords } from "../util/bannedWords";
import { thumbUrl, displayUrl, imgFallback } from "../util/imageUrl";

// const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

Array.prototype.sample = function () {
  return this[Math.floor(Math.random() * this.length)];
};

const generateSeed = () => {
  let randomBytes = new Uint8Array(2);
  window.crypto.getRandomValues(randomBytes);

  const seed = (randomBytes[0] << 8) | randomBytes[1];
  return seed;
};

const useStyles = makeStyles((theme) => ({
  media: {
    height: 160,
  },
}));

function Generator(props) {
  const classes = useStyles();
  const auth = useAuth();
  const router = useRouter();
  const [prediction, setPrediction] = useState(null);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const [adjustedUserPrompt, setAdjustedUserPrompt] = useState("");
  const [adjustedUserNegativePrompt, setAdjustedUserNeagtivePrompt] =
    useState("");
  const [showWarning, setShowWarning] = useState(false);
  const [loaderOpen, setLoaderOpen] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [bannedWords, setBannedWords] = useState({});
  const [isBannedPopupOpen, setIsBannedPopupOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [inputData, setInputData] = useState({
    prompt: router.query.prompt || "beautiful brunette girl",
    negativePrompt: router.query.negativePrompt || "",
    style: router.query.style || "real",
    pose: router.query.pose || "Default",
    lora: router.query.lora || "",
    weight: router.query.weight || 1.2,
    isPrivate: false,
  });

  useEffect(() => {
    const loadBannedWords = async () => {
      const words = await fetchBannedWords();
      console.log("Loaded Banned Words:", words);
      setBannedWords(words);
    };
    loadBannedWords();
  }, []);

  const shouldBlockContent = (promptText) => {
    if (!bannedWords) return false;

    const text = promptText.toLowerCase();
    const matchedCategories = [];

    const checkTerms = (terms, category) => {
      for (const term of terms || []) {
        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`\\b${escapedTerm}\\b`, "i");
        if (regex.test(text)) {
          matchedCategories.push(category);
          return true;
        }
      }
      return false;
    };

    for (const category in bannedWords) {
      const terms = bannedWords[category];
      if (category === "Always Block" && checkTerms(terms, category)) {
        return true; // block immediately
      } else {
        checkTerms(terms, category); // track other matches
      }
    }

    const sexualMatch = matchedCategories.includes("Sexual Terms");
    const alcoholDrugMatch = matchedCategories.includes("Alcohol Drug Terms");
    const moneyCrimeMatch = matchedCategories.includes("Money Crime Terms");
    const weaponsMatch = matchedCategories.includes("Weapons Terms");

    // Block only if sexual term matches with alcohol/drugs, money, or weapons
    if (sexualMatch && (alcoholDrugMatch || moneyCrimeMatch || weaponsMatch)) {
      return true;
    }

    return false;
  };

  const { prompt, negativePrompt, style, pose, isPrivate, lora, weight } =
    inputData;

  const handleChange = (event) => {
  const { name, value } = event.target;

  if (name === "pose") {
    const poseConfig = modelPoses.find((p) => p.value === value);
    setInputData((prevData) => ({
      ...prevData,
      pose: value,
      lora: poseConfig?.lora || "",
      weight: poseConfig?.weight || 1.2,
    }));
  } else {
    setInputData((prevData) => ({ ...prevData, [name]: value }));
  }
};

  const handleStyleClick = (style) => {
    setInputData((prevData) => ({ ...prevData, style: style }));
  };

  const handlePoseClick = (pose, lora, weight) => {
    setInputData((prevData) => ({ ...prevData, pose: pose }));
    setInputData((prevData) => ({ ...prevData, lora: lora }));
    setInputData((prevData) => ({ ...prevData, weight: weight }));
  };

  const handleSwitch = (event) => {
    if (auth.user && auth.user.planIsActive) {
      setInputData((prevData) => ({
        ...prevData,
        [event.target.name]: event.target.checked,
      }));
    } else {
      alert("Upgrade to pro to use this feature");
    }
  };

  const modelStyles = [
    {
      image: "/cloudImages/girl1.webp",
      value: "real",
      text: "Real",
      goodPrompt:
        "RAW photo, SUBJECT, 8k uhd, dslr, soft lighting, high quality, film grain, Fujifilm XT3",
      badPrompt:
        "(easynegative, ng_deepnegative_v1_75t, SUBJECT, deformed iris, deformed pupils, semi-realistic, cgi, 3d, render, sketch, cartoon, drawing, anime), text, cropped, out of frame, worst quality, low quality, jpeg artifacts, ugly, duplicate, morbid, mutilated, extra fingers, mutated hands, poorly drawn hands, poorly drawn face, mutation, deformed, blurry, dehydrated, bad anatomy, bad proportions, extra limbs, cloned face, disfigured, gross proportions, malformed limbs, missing arms, missing legs, extra arms, extra legs, fused fingers, too many fingers, long neck, UnrealisticDream",
    },
    {
      image: "/cloudImages/girl2.webp",
      value: "hyperreal",
      text: "Hyper Real",
      goodPrompt:
        "((photo, high quality, high resolution, 4k, hd, hdr, dslr)), 1girl, SUBJECT",
      badPrompt:
        "easynegative, ng_deepnegative_v1_75t, SUBJECT, paintings, sketches, digital arts, (worst quality:2), (low quality:2), (normal quality:2), low resolution, ((monochrome)), ((grayscale)), watermark, out of frame, (ugly:1.2), people",
    },
    {
      image: "https://i.imgur.com/Ya2D8Jm.png",
      value: "anime",
      text: "Anime",
      goodPrompt: "masterpiece, best quality, 1girl, SUBJECT",
      badPrompt:
        "easynegative, ng_deepnegative_v1_75t, SUBJECT, (low quality, worst quality:1.4), lowres, bad anatomy, bad hands, normal quality, ((monochrome)), ((grayscale))",
    },
  ];

  const modelPoses = [
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_d0b5e62f.webp",
      text: "Default",
      value: "Default",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_b5323c1e.webp",
      text: "POV BlowJob",
      value: "POVBlowJob",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_ab638994.webp",
      text: "POV DoggyStyle",
      value: "POVDoggyStyle",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_db8a21f2.webp",
      text: "HandJob",
      value: "HandJob",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_e809831e.webp",
      text: "Flashing Boobs",
      value: "FlashingBoobs",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_01a95af7.webp",
      text: "Stick Out Tongue",
      value: "StickOutTongue",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_c2b4ca76.webp",
      text: "Grab Ass",
      value: "GrabAss",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_0f6ceb52.webp",
      text: "POV Anal",
      value: "POVAnal",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_e6c04d85.webp",
      text: "Vagina Spread",
      value: "VaginaAndAnus",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_345ebfed.webp",
      text: "Female Masturbation",
      value: "FemaleMasturbation",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_b9544410.webp",
      text: "TitJob",
      value: "TitJob",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_5997cac4.webp",
      text: "Bukkake",
      value: "Bukkake",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_6a7f0908.webp",
      text: "Spooning",
      value: "Spooning",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_bc7df8a1.webp",
      text: "POV Missionary",
      value: "POVMissionary",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_0f7cc267.webp",
      text: "Cheek Bulge BlowJob",
      value: "CheekBulgeBlowJob",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_f3c55ce5.webp",
      text: "Solo Breast Grab",
      value: "SoloBreastGrab",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_49bdeb1d.webp",
      text: "BlowJob Under Desk",
      value: "BlowJobUnderDesk",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_b9f4e66d.webp",
      text: "Front View CowGirl",
      value: "FrontViewCowgirl",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_6d2a9cc6.webp",
      text: "POV SpitRoast",
      value: "POVSpitRoast",
      lora: "",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_d29da763.webp",
      text: "Titjob",
      value: "TitJob",
      lora: "TitjobBlowJobTitjob.safetensors",
      weight: 0.8,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_8e252d9a.webp",
      text: "Forward Position",
      value: "ForwardPosition",
      lora: "FS_Forward-000009.safetensors",
      weight: 1,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_9bb3ba6c.webp",
      text: "Deep Throat",
      value: "Deepthroat",
      lora: "Deep_Throat_4-000013.safetensors",
      weight: 0.9,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_24ccd58c.webp",
      text: "Gangbang Blowjob",
      value: "GangbangBlowjob",
      lora: "gangbang_blowjob_sdxl-4416-24_v1.safetensors",
      weight: 0.8,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_590cf15b.webp",
      text: "Spread Ass",
      value: "SpreadAss",
      lora: "SpreadAssSDXL_v04.safetensors",
      weight: 1,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_ed3210a4.webp",
      text: "Finger Ass",
      value: "FingerAss",
      lora: "finger_ass.safetensors",
      weight: 0.9,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_0f79bf20.webp",
      text: "Reverse Cowgirl Full Nelson",
      value: "POVReverseCowGirl",
      lora: "ReverseCowgirlFullNelsonXL.safetensors",
      weight: 1,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_82274a98.webp",
      text: "Anal",
      value: "Anal",
      lora: "analSDXL.safetensors",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_c348efcd.webp",
      text: "Duct Tape",
      value: "Ducttape",
      lora: "ducttape.safetensors",
      weight: 1,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_b3d01e3a.webp",
      text: "Huge Tits",
      value: "Hugetits",
      lora: "hugetits.safetensors",
      weight: 1,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_5f19fc02.webp",
      text: "Massive Tits",
      value: "MassiveTits",
      lora: "massivetits.safetensors",
      weight: 1,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_214cad1f.webp",
      text: "Breast Clamp",
      value: "breast clamp, nipple clamps, BDSM",
      lora: "breastclamp.safetensors",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_902cbd0b.webp",
      text: "Giant Saggy Breast",
      value: "GiantSaggyBreast",
      lora: "giantsaggybreast.safetensors",
      weight: 1,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_054770c4.webp",
      text: "Blindfold",
      value: "Blindfold",
      lora: "blindfold.safetensors",
      weight: 1,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/default_images/deepthroat2.jpg",
      text: "Deepthroat",
      value: "Deepthroat",
      lora: "deepthroat.safetensors",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_f7d538ea.webp",
      text: "Gaping Asshole",
      value: "AnalGape",
      lora: "gaping_asshole.safetensors",
      weight: 1,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_b56d6d7d.webp",
      text: "Cum Swap",
      value: "Cumswap",
      lora: "cumswap.safetensors",
      weight: 1.2,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_53b6de14.webp",
      text: "Pregnant",
      value: "Pregnant",
      lora: "pregnant.safetensors",
      weight: 1,
    },
    {
      image:
        "https://storage.googleapis.com/xgenerator-project.appspot.com/users_img/generated_image_40ba01d9.webp",
      text: "Pussy Gape",
      value: "PussyGape",
      lora: "pussy_gape.safetensors",
      weight: 1.2,
    },
  ];

  const wordList = {
    abuse: "party",
    "acid attack": "adult",
    adolescent: "adult",
    babyish: "adult",
    baby: "young",
    "black market": "adult",
    bullying: "adult",
    callow: "adult",
    child: "adult",
    little: "adult",
    childish: "adult",
    Child: "adult",
    childlike: "adult",
    children: "adult",
    contraband: "adult",
    "criminal behavior": "adult",
    embryonic: "adult",
    ephebic: "adult",
    exploitation: "adult",
    felony: "adult",
    flowering: "adult",
    forced: "adult",
    fraud: "adult",
    gore: "dancing",
    harassment: "adult",
    trafficking: "adult",
    illegal: "adult",
    illicit: "adult",
    immature: "adult",
    assault: "adult",
    infanticide: "adult",
    infantile: "adult",
    infantine: "adult",
    juvenile: "adult",
    kiddish: "adult",
    lawbreaking: "adult",
    molestation: "adult",
    "money laundering": "adult",
    petite: "adult",
    kid: "adult",
    small: "size",
    preteen: "adult",
    preteens: "adult",
    prostitution: "adult",
    pubescent: "adult",
    puerile: "adult",
    rape: "adult",
    raping: "adult",
    ravishment: "adult",
    raw: "adult",
    school: "office",
    assault: "playing",
    harassment: "playing",
    stalking: "adult",
    teen: "adult",
    teenage: "adult",
    teenaged: "adult",
    teens: "adult",
    "under 18": "21+",
    underage: "adult",
    unethical: "adult",
    unfledged: "adult",
    unlawful: "adult",
    unripe: "adult",
    unripened: "adult",
    wrongful: "adult",
    youngish: "adult",
    young: "adult",
    Pre: "",
    Very: "",
    kindergarten: "",
    kinder: "",
  };

  const generate = async (_prompt, _pose, _style) => {
    try {
      if (shouldBlockContent(prompt)) {
        setIsBannedPopupOpen(true);
        return;
      }
      setLoaderOpen(true);
      setPending(true);
      const seed = generateSeed();
      const adjustedNegativePrompt = processPromptWithWordList(
        negativePrompt,
        wordList
      );

      if (negativePrompt !== adjustedNegativePrompt) {
        setAdjustedUserNeagtivePrompt(adjustedNegativePrompt);
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }

      const isSafe = "NO";
      if (isSafe === "YES") {
        setShowWarning(true);
        setPending(false);
        return;
      }

      const inputData = {
        prompt: prompt,
        negativePrompt: adjustedNegativePrompt,
        pose: _pose || pose,
        style: _style || style,
        seed: seed,
        lora: lora,
        weight: weight,
      };

      let prediction = await modifiedApiRequest(
        "modified-predictions",
        "POST",
        inputData
      );

      setPrediction(prediction);
      setPending(false);

      if (
        prediction.status === "COMPLETED" &&
        prediction.output[0].finish_reason === "SUCCESS"
      ) {
        createItem({
          owner: auth.user.uid,
          ownerName: auth.user.displayName,
          userPrompt: _prompt || prompt,
          userNegativePrompt: adjustedNegativePrompt,
          style: _style || style,
          pose: _pose || pose,
          isPrivate: isPrivate,
          isNSFW: true,
          url: prediction.output[0].url,
        });
      } else if (
        prediction.status === "COMPLETED" &&
        prediction.output[0].url === ""
      ) {
        if (retryCount >= 2) {
          console.log("Max retries reached. Exiting...");
          return;
        }

        setRetryCount((prevCount) => prevCount + 1);

        let pred = await modifiedApiRequest(
          "modified-predictions",
          "POST",
          inputData
        );

        createItem({
          owner: auth.user.uid,
          ownerName: auth.user.displayName,
          userPrompt: _prompt || prompt,
          userNegativePrompt: adjustedNegativePrompt,
          style: _style || style,
          pose: _pose || pose,
          isPrivate: isPrivate,
          isNSFW: true,
          url: pred.output[0].url,
        });
      }
    } catch (error) {
      setPending(false);
      console.error("Error during generation:", error.message);
    } finally {
      setLoaderOpen(false);
    }
  };

  const processPromptWithWordList = (prompt, wordList) => {
    let newText = String(prompt); // Ensure newText is a string
    for (const term in wordList) {
      const replacement = wordList[term];
      const escapedTerm = term.replace(/ /g, "\\s*"); // Replace spaces with regex for zero or more spaces
      const regex = new RegExp(escapedTerm, "gi"); // Match the term or phrase with spaces, case-insensitive
      newText = newText.replace(regex, String(replacement)); // Ensure replacement is a string
    }
    newText = newText.replace(/(\d{2}|[1-9])/g, (match) => {
      if (match.length === 2) {
        return "20+ ";
      } else if (match.length === 1) {
        return "20+ ";
      }
    });
    return newText;
  };

  const handleGenerate = () => {
    if (auth?.user?.credit < 0 && auth?.user?.extraCredit < 0) {
      alert("Not enough credits");
      return;
    }
    generate();
  };

  const handleDownload = () => {
    const downloadUrl = `/api/download?url=${encodeURIComponent(
      prediction?.output[0]?.url
    )}`;
    window.open(downloadUrl, "_blank");
  };

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()}>
        <Grid container spacing={4}>
          {/* LEFT COLUMN: controls */}
          <Grid item xs={12} md={7}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography
                  variant="overline"
                  style={{
                    display: "block",
                    marginBottom: 8,
                    letterSpacing: 1,
                    opacity: 0.7,
                  }}
                >
                  Describe your image
                </Typography>
                <TextField
                  name="prompt"
                  variant="outlined"
                  multiline
                  minRows={4}
                  placeholder="e.g. beautiful brunette girl, soft window light, detailed eyes"
                  value={prompt}
                  onChange={handleChange}
                  fullWidth
                />
                <Box mt={1}>
                  <Button
                    size="small"
                    onClick={() => setShowAdvanced((v) => !v)}
                    style={{ textTransform: "none", padding: "4px 0" }}
                    endIcon={
                      showAdvanced ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )
                    }
                  >
                    {showAdvanced
                      ? "Hide advanced"
                      : "Advanced (negative prompt)"}
                  </Button>
                  {showAdvanced && (
                    <Box mt={2}>
                      <TextField
                        name="negativePrompt"
                        variant="outlined"
                        multiline
                        minRows={2}
                        label="Negative prompt"
                        placeholder="ugly, hat, earring, blurry"
                        value={negativePrompt}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="overline"
                  style={{
                    display: "block",
                    marginBottom: 8,
                    letterSpacing: 1,
                    opacity: 0.7,
                  }}
                >
                  Style
                </Typography>
                <Grid container spacing={2}>
                  {modelStyles.map((item, index) => {
                    const selected = item.value === style;
                    return (
                      <Grid item xs={4} key={index}>
                        <Card
                          style={{
                            outline: selected
                              ? "2px solid rgba(244, 81, 30, 0.9)"
                              : "2px solid transparent",
                            transition: "outline-color 120ms ease",
                          }}
                        >
                          <CardActionArea
                            onClick={() => handleStyleClick(item.value)}
                          >
                            <Box style={{ position: "relative" }}>
                              {/* 130px selector tile — these 38 entries are
                                  full-size bucket PNGs (~1.2 MB each), so the
                                  /create page pulled ~45 MB before this. */}
                              <CardMedia
                                component="img"
                                style={{ height: 130, width: "100%", objectFit: "cover", display: "block" }}
                                src={thumbUrl(item.image)}
                                onError={imgFallback(item.image)}
                                loading="lazy"
                                decoding="async"
                                alt={item.text || item.value || ""}
                                className={classes.media}
                              />
                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: 0,
                                  left: 0,
                                  width: "100%",
                                  bgcolor: selected
                                    ? "primary.dark"
                                    : "rgba(0, 0, 0, 0.54)",
                                  color: "white",
                                  padding: "5px",
                                  textAlign: "center",
                                }}
                              >
                                <Typography variant="caption">
                                  {item.text}
                                </Typography>
                              </Box>
                            </Box>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="overline"
                  style={{
                    display: "block",
                    marginBottom: 8,
                    letterSpacing: 1,
                    opacity: 0.7,
                  }}
                >
                  Pose
                </Typography>
                <Box
                  style={{
                    maxHeight: 460,
                    overflowY: "auto",
                    paddingRight: 8,
                    paddingBottom: 4,
                  }}
                >
                  <Grid container spacing={2}>
                    {modelPoses.map((item, index) => {
                      const selected = item.value === pose;
                      return (
                        <Grid item xs={6} sm={4} key={index}>
                          <Card
                            style={{
                              outline: selected
                                ? "2px solid rgba(244, 81, 30, 0.9)"
                                : "2px solid transparent",
                              transition: "outline-color 120ms ease",
                            }}
                          >
                            <CardActionArea
                              onClick={() =>
                                handlePoseClick(
                                  item.value,
                                  item.lora,
                                  item.weight
                                )
                              }
                            >
                              <Box style={{ position: "relative" }}>
                                {/* 130px selector tile — see note above. */}
                                <CardMedia
                                  component="img"
                                  style={{ height: 130, width: "100%", objectFit: "cover", display: "block" }}
                                  src={thumbUrl(item.image)}
                                  onError={imgFallback(item.image)}
                                  loading="lazy"
                                  decoding="async"
                                  alt={item.text || item.value || ""}
                                  className={classes.media}
                                />
                                <Box
                                  sx={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    width: "100%",
                                    bgcolor: selected
                                      ? "primary.dark"
                                      : "rgba(0, 0, 0, 0.54)",
                                    color: "white",
                                    padding: "5px",
                                    textAlign: "center",
                                  }}
                                >
                                  <Typography variant="caption">
                                    {item.text}
                                  </Typography>
                                </Box>
                              </Box>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isPrivate}
                      onChange={handleSwitch}
                      name="isPrivate"
                      color="primary"
                    />
                  }
                  label="Keep image private (Pro only)"
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleGenerate}
                  disabled={pending}
                  fullWidth
                  startIcon={!pending && <Sparkles size={18} />}
                  style={{
                    paddingTop: 14,
                    paddingBottom: 14,
                    fontSize: 16,
                    fontWeight: 600,
                  }}
                >
                  {!pending && <span>{props.buttonAction}</span>}
                  {pending && <CircularProgress size={26} />}
                </Button>
              </Grid>

              {showWarning && (
                <Grid item xs={12}>
                  <PromptWarning
                    prompt={prompt}
                    improvedPrompt={adjustedUserPrompt}
                    negativePrompt={negativePrompt}
                    improvedNegativePrompt={adjustedUserNegativePrompt}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    borderRadius: "10px",
                    padding: "12px 14px",
                    border: "1px solid #1595a9",
                    color: "#117b8c",
                    background: "rgba(23, 162, 184, 0.15)",
                  }}
                >
                  <Typography variant="caption" style={{ fontSize: 11 }}>
                    <CheckBoxOutlined
                      style={{
                        color: "#117b8c",
                        fontSize: 12,
                        verticalAlign: "middle",
                      }}
                    />{" "}
                    Default age is 25 unless one is specified.
                  </Typography>
                  <Typography variant="caption" style={{ fontSize: 11 }}>
                    <CheckBoxOutlined
                      style={{
                        color: "#117b8c",
                        fontSize: 12,
                        verticalAlign: "middle",
                      }}
                    />{" "}
                    Violence, gore, celebrity names, and minors are prohibited
                    and will be removed.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* RIGHT COLUMN: sticky preview */}
          <Grid item xs={12} md={5}>
            <Box style={{ position: "sticky", top: 16 }}>
              <Box
                style={{
                  width: "100%",
                  position: "relative",
                  paddingTop: "128.57%",
                  borderRadius: 12,
                  border: "2px dashed rgba(255,255,255,0.15)",
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
                  overflow: "hidden",
                }}
              >
                <Box
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {prediction &&
                  prediction.status === "COMPLETED" &&
                  prediction.output?.[0]?.url ? (
                    <>
                      {/* Preview panel, not the download. Serve the 1024px
                          rendition; handleDownload and the "Animate this
                          image" handoff below both keep the full-resolution
                          original. */}
                      <img
                        src={displayUrl(prediction.output[0].url)}
                        onError={imgFallback(prediction.output[0].url)}
                        decoding="async"
                        alt="Generated"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      <IconButton
                        onClick={handleDownload}
                        aria-label="Download image"
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          background: "rgba(255,255,255,0.85)",
                        }}
                      >
                        <Download className="h-5 w-5 text-gray-700" />
                      </IconButton>
                    </>
                  ) : pending ? (
                    <Box
                      style={{
                        textAlign: "center",
                        color: "rgba(255,255,255,0.7)",
                      }}
                    >
                      <CircularProgress />
                      <Typography
                        variant="body2"
                        style={{ marginTop: 12, opacity: 0.8 }}
                      >
                        Painting your fantasy…
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      style={{
                        textAlign: "center",
                        color: "rgba(255,255,255,0.5)",
                        padding: "0 24px",
                      }}
                    >
                      <ImageIcon size={56} strokeWidth={1.25} />
                      <Typography
                        variant="body2"
                        style={{ marginTop: 12, fontWeight: 500 }}
                      >
                        Your image will appear here
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{
                          display: "block",
                          marginTop: 6,
                          opacity: 0.75,
                        }}
                      >
                        Pick a style, describe your fantasy, then hit Generate.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {prediction &&
                prediction.status === "COMPLETED" &&
                prediction.output?.[0]?.url && (
                  <>
                    <Box mt={2} display="flex" style={{ gap: 12 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleDownload}
                        startIcon={<Download size={16} />}
                      >
                        Download
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleGenerate}
                        disabled={pending}
                        startIcon={<RefreshCw size={16} />}
                      >
                        Regenerate
                      </Button>
                    </Box>
                    <Box mt={1.5}>
                      <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        onClick={() =>
                          router.push(
                            `/create-video?imageUrl=${encodeURIComponent(
                              prediction.output[0].url
                            )}`
                          )
                        }
                        startIcon={<VideoIcon size={16} />}
                      >
                        Animate this image
                      </Button>
                      <Typography
                        variant="caption"
                        style={{
                          display: "block",
                          textAlign: "center",
                          marginTop: 6,
                          opacity: 0.75,
                        }}
                      >
                        Turn it into a 5-second video — costs 10 credits
                        (subscribers only)
                      </Typography>
                    </Box>
                  </>
                )}
            </Box>
          </Grid>
        </Grid>
      </form>
      <TimerLoader open={loaderOpen} onClose={() => setLoaderOpen(false)} />
      {isBannedPopupOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 z-[9999]">
          <div className="bg-[#2c2c2c] rounded-xl p-6 max-w-md w-[90%] text-center shadow-lg">
            <Typography
              variant="h6"
              className="text-[#ff4444] mb-4 font-semibold"
            >
              ⚠️ Restricted Content
            </Typography>
            <Typography variant="body1" className="text-[#d0d0d0] mb-6">
              Sorry, I can't generate images with that request. Please enter a
              safe and compliant prompt.
            </Typography>
            <Button
              onClick={() => setIsBannedPopupOpen(false)}
              variant="contained"
              className="bg-[#ff4251] text-white w-full py-2.5 normal-case font-medium"
            >
              Okay
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default Generator;
